import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, SlidersHorizontal, X, ChevronDown, ShieldCheck, Bookmark } from 'lucide-react';
import Layout from '../components/layout/Layout';
import ProductCard from '../components/product/ProductCard';
import SEO from '../components/common/SEO';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Slider } from '../components/ui/slider';
import { trackSearch } from '../services/analytics';
import { COUNTRY_OPTIONS, getViewerCountry, productCountry } from '../utils/regionUtils';
import { useCurrency } from '../context/CurrencyContext';
import AgeGateModal from './AgeGateModal';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const BrowseProductsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { products, categories, user, isAuthenticated } = useApp();
  const { rates, country: viewerCountryCtx } = useCurrency();

  // Normalise a product price to AUD for fair sorting/filtering across currencies
  const priceAUD = (p) => {
    const c = (p.listing_currency || 'AUD').toUpperCase();
    if (c === 'AUD' || !rates[c]) return p.price;
    return p.price / rates[c];
  };
  
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [sortBy, setSortBy] = useState('newest');
  const [showAgeGate, setShowAgeGate] = useState(false);
  const [ageVerified, setAgeVerified] = useState(user?.age_verified || false);
  const [adultProducts, setAdultProducts] = useState([]);

  // Sync search query with URL params
  React.useEffect(() => {
    const q = searchParams.get('q');
    if (q !== null && q !== searchQuery) {
      setSearchQuery(q);
    }
  }, [searchParams]);

  // Check age verification status on mount
  React.useEffect(() => {
    if (user?.age_verified) setAgeVerified(true);
  }, [user]);

  // When category changes to 18+, check age verification
  React.useEffect(() => {
    const cat = searchParams.get('category');
    if (cat === '18+ Adults Only') {
      navigate('/private-collection', { replace: true });
      return;
    }
    if (false) {
      if (!isAuthenticated) {
        navigate('/browse?category=all');
        return;
      }
      if (!ageVerified && !user?.age_verified) {
        setShowAgeGate(true);
      } else {
        // Fetch adult products
        fetchAdultProducts();
      }
    }
  }, [searchParams.get('category'), ageVerified]);

  const fetchAdultProducts = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/products?category=18%2B+Adults+Only&include_adult=true`);
      const data = await res.json();
      setAdultProducts(Array.isArray(data) ? data : []);
    } catch {
      setAdultProducts([]);
    }
  };

  const handleAgeVerified = () => {
    setAgeVerified(true);
    fetchAdultProducts();
  };
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || 'all',
    condition: 'all',
    priceMin: 0,
    priceMax: 999999,
    shippingAvailable: false,
    pickupAvailable: searchParams.get('pickup') === 'true',
    state: searchParams.get('state') || 'all',
    country: searchParams.get('country') || getViewerCountry() || 'all'
  });

  // Filter out example products, sold items, and adult items (unless in 18+ category)
  const availableProducts = products.filter(p => !p.sold && !p.is_example && !p.is_adult);

  // Apply filters and search
  const filteredProducts = useMemo(() => {
    // Use adult products when viewing 18+ category
    const isAdultCategory = filters.category === '18+ Adults Only';
    let result = isAdultCategory ? [...adultProducts] : [...availableProducts];

    // Search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(p => 
        p.title?.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query) ||
        p.category?.toLowerCase().includes(query)
      );
    }

    // Category filter (skip for 18+ since already filtered from API)
    if (filters.category && filters.category !== 'all' && !isAdultCategory) {
      result = result.filter(p => p.category === filters.category);
    }

    // Condition filter
    if (filters.condition && filters.condition !== 'all') {
      result = result.filter(p => p.condition === filters.condition);
    }

    // Price filter (normalised to AUD so GBP/EUR listings compare fairly)
    result = result.filter(p => {
      const aud = priceAUD(p);
      return aud >= filters.priceMin && aud <= filters.priceMax;
    });

    // Delivery filters — if both are ticked, show items offering EITHER option
    const offersShipping = (p) => p.shipping_available || p.shippingAvailable;
    const offersPickup = (p) => p.pickup_available || p.pickupAvailable;
    if (filters.shippingAvailable && filters.pickupAvailable) {
      result = result.filter(p => offersShipping(p) || offersPickup(p));
    } else if (filters.shippingAvailable) {
      result = result.filter(offersShipping);
    } else if (filters.pickupAvailable) {
      result = result.filter(offersPickup);
    }

    // Country filter (seller's region)
    if (filters.country && filters.country !== 'all') {
      result = result.filter(p => productCountry(p) === filters.country);
    }

    // State filter — only meaningful within Australia
    if (filters.state && filters.state !== 'all' && (filters.country === 'Australia' || filters.country === 'all')) {
      const st = filters.state.toUpperCase();
      result = result.filter(p =>
        (p.state || '').toUpperCase() === st ||
        (p.location || '').toUpperCase().includes(st)
      );
    }

    // Sort
    switch (sortBy) {
      case 'newest': {
        const viewer = viewerCountryCtx || getViewerCountry();
        result.sort((a, b) => {
          const boost = (b.is_boosted === true) - (a.is_boosted === true);
          if (boost) return boost;
          if (viewer && filters.country === 'all') {
            const local = (productCountry(b) === viewer) - (productCountry(a) === viewer);
            if (local) return local;
          }
          return new Date(b.created_at || b.createdAt) - new Date(a.created_at || a.createdAt);
        });
        break;
      }
      case 'price-low':
        result.sort((a, b) => priceAUD(a) - priceAUD(b));
        break;
      case 'price-high':
        result.sort((a, b) => priceAUD(b) - priceAUD(a));
        break;
      case 'most-viewed':
        result.sort((a, b) => (b.views || 0) - (a.views || 0));
        break;
      default:
        break;
    }

    return result;
  }, [availableProducts, searchQuery, filters, sortBy, rates, viewerCountryCtx]);

  // Apply first-visit detected country once it arrives (unless the user already chose)
  const countryAppliedRef = React.useRef(!!(searchParams.get('country') || getViewerCountry()));
  React.useEffect(() => {
    if (countryAppliedRef.current || !viewerCountryCtx) return;
    countryAppliedRef.current = true;
    setFilters(prev => prev.country === 'all' ? { ...prev, country: viewerCountryCtx } : prev);
  }, [viewerCountryCtx]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      trackSearch(searchQuery);
      setSearchParams({ q: searchQuery });
    }
  };

  const clearFilters = () => {
    setFilters({
      category: 'all',
      condition: 'all',
      priceMin: 0,
      priceMax: 999999,
      shippingAvailable: false,
      pickupAvailable: false,
      state: 'all',
      country: 'all'
    });
    setSearchQuery('');
    setSearchParams({});
  };

  const conditions = ['New', 'Like New', 'Excellent', 'Very Good', 'Good', 'Fair'];
  const hasActiveFilters = filters.category !== 'all' || filters.condition !== 'all' || 
    filters.priceMin > 0 || filters.priceMax < 999999 || filters.shippingAvailable || filters.pickupAvailable || filters.state !== 'all' || filters.country !== 'all' || searchQuery;

  return (
    <Layout showBack>
      {/* SEO for Browse Products */}
      <SEO 
        title="Browse All Products"
        description="Browse thousands of preloved items for sale near you. Find deals on clothing, electronics, furniture, toys and more. Filter by category, price, and condition."
        url="/products"
        breadcrumbs={[{ name: 'Browse Products', url: '/products' }]}
      />
      <div className="min-h-screen bg-gray-50 pb-20">
        {/* Search Header */}
        <div className="bg-white border-b sticky top-14 z-30 px-4 py-3">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products..."
                className="pl-10 pr-10"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  <X className="w-4 h-4 text-gray-400" />
                </button>
              )}
            </div>
            
            <Sheet open={showFilters} onOpenChange={setShowFilters}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="relative">
                  <SlidersHorizontal className="w-4 h-4" />
                  {hasActiveFilters && (
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-minted-500 rounded-full" />
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80">
                <SheetHeader>
                  <SheetTitle>Filters</SheetTitle>
                </SheetHeader>
                
                <div className="mt-6 space-y-6">
                  {/* Category */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">Category</label>
                    <Select value={filters.category} onValueChange={(v) => {
                      if (v === '18+ Adults Only') {
                        navigate('/private-collection');
                        return;
                      }
                      if (false) {
                        if (!isAuthenticated) {
                          navigate('/');
                          return;
                        }
                        if (!ageVerified && !user?.age_verified) {
                          setShowAgeGate(true);
                          setFilters({...filters, category: v});
                          return;
                        }
                        setFilters({...filters, category: v});
                        fetchAdultProducts();
                      } else {
                        setFilters({...filters, category: v});
                      }
                    }}>
                      <SelectTrigger>
                        <SelectValue placeholder="All Categories" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {categories.map(cat => (
                          <SelectItem key={cat.id} value={cat.name}>
                            {cat.is_adult ? `${cat.name}` : cat.name}
                          </SelectItem>
                        ))}
                        <SelectItem value="18+ Adults Only" data-testid="browse-cat-private-collection">
                          🗝️ The Private Collection (18+)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Condition */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">Condition</label>
                    <Select value={filters.condition} onValueChange={(v) => setFilters({...filters, condition: v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Any Condition" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Any Condition</SelectItem>
                        {conditions.map(cond => (
                          <SelectItem key={cond} value={cond}>{cond}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Price Range */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      Price (A$): ${filters.priceMin.toLocaleString()} – {filters.priceMax >= 999999 ? 'Any' : '$' + filters.priceMax.toLocaleString()}
                    </label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        placeholder="Min"
                        value={filters.priceMin || ''}
                        onChange={(e) => setFilters({...filters, priceMin: parseInt(e.target.value) || 0})}
                        className="w-24"
                      />
                      <span className="text-gray-400 self-center">to</span>
                      <Input
                        type="number"
                        placeholder="Max"
                        value={filters.priceMax >= 999999 ? '' : filters.priceMax}
                        onChange={(e) => setFilters({...filters, priceMax: parseInt(e.target.value) || 999999})}
                        className="w-24"
                      />
                    </div>
                  </div>

                  {/* Shipping */}
                  <div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.shippingAvailable}
                        onChange={(e) => setFilters({...filters, shippingAvailable: e.target.checked})}
                        className="rounded border-gray-300 text-minted-600 focus:ring-minted-500"
                      />
                      <span className="text-sm">Shipping Available</span>
                    </label>
                  </div>

                  {/* Pickup */}
                  <div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.pickupAvailable}
                        onChange={(e) => setFilters({...filters, pickupAvailable: e.target.checked})}
                        className="rounded border-gray-300 text-minted-600 focus:ring-minted-500"
                        data-testid="pickup-filter-toggle"
                      />
                      <span className="text-sm">Pickup Available</span>
                    </label>
                  </div>

                  {/* Country */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-1.5">Country</label>
                    <select
                      value={filters.country}
                      onChange={(e) => setFilters({...filters, country: e.target.value, state: 'all'})}
                      className="w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm"
                      data-testid="country-filter-select"
                    >
                      <option value="all">All countries</option>
                      {COUNTRY_OPTIONS.map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>

                  {/* State (Australia only) */}
                  {(filters.country === 'Australia' || filters.country === 'all') && (
                    <div>
                      <label className="text-sm font-medium text-gray-700 block mb-1.5">Australian State</label>
                      <select
                        value={filters.state}
                        onChange={(e) => setFilters({...filters, state: e.target.value})}
                        className="w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm"
                        data-testid="state-filter-select"
                      >
                        <option value="all">All states</option>
                        {['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'].map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 pt-4">
                    <Button variant="outline" className="flex-1" onClick={clearFilters}>
                      Clear All
                    </Button>
                    <Button className="flex-1 bg-minted-600 hover:bg-minted-700" onClick={() => setShowFilters(false)}>
                      Apply
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </form>

          {/* Sort & Results Count */}
          <div className="flex items-center justify-between mt-3">
            <p className="text-sm text-gray-600">
              {filteredProducts.length} {filteredProducts.length === 1 ? 'result' : 'results'}
              {filters.country !== 'all' && (
                <span className="text-gray-500"> in {filters.country} · <button type="button" onClick={() => setFilters({...filters, country: 'all'})} className="text-minted-600 font-medium underline" data-testid="show-all-countries-btn">show all countries</button></span>
              )}
            </p>
            <div className="flex items-center gap-2">
              {isAuthenticated && (
                <button
                  onClick={async () => {
                    try {
                      const res = await fetch(`${API_BASE}/api/saved-searches`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          user_id: user.id,
                          query: searchQuery,
                          category: filters.category,
                          condition: filters.condition,
                          price_min: filters.priceMin,
                          price_max: filters.priceMax,
                          shipping_available: filters.shippingAvailable,
                          notify: true,
                        })
                      });
                      if (res.ok) {
                        toast.success('Search saved! You\'ll be notified of new matches.');
                      } else {
                        const err = await res.json().catch(() => ({}));
                        toast.error(err.detail || 'Failed to save search');
                      }
                    } catch { toast.error('Failed to save search'); }
                  }}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-minted-700 hover:bg-minted-50 rounded-sm transition-colors"
                  data-testid="save-search-btn"
                >
                  <Bookmark className="w-3.5 h-3.5" />
                  Save
                </button>
              )}
              <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-36 h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest First</SelectItem>
                <SelectItem value="price-low">Price: Low to High</SelectItem>
                <SelectItem value="price-high">Price: High to Low</SelectItem>
                <SelectItem value="most-viewed">Most Viewed</SelectItem>
              </SelectContent>
            </Select>
            </div>
          </div>

          {/* Active Filters Tags */}
          {hasActiveFilters && (
            <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
              {searchQuery && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-minted-100 text-minted-700 text-xs rounded-full">
                  "{searchQuery}"
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setSearchQuery('')} />
                </span>
              )}
              {filters.category !== 'all' && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-minted-100 text-minted-700 text-xs rounded-full">
                  {filters.category}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setFilters({...filters, category: 'all'})} />
                </span>
              )}
              {filters.condition !== 'all' && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-minted-100 text-minted-700 text-xs rounded-full">
                  {filters.condition}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setFilters({...filters, condition: 'all'})} />
                </span>
              )}
              <button onClick={clearFilters} className="text-xs text-gray-500 hover:text-gray-700 whitespace-nowrap">
                Clear all
              </button>
            </div>
          )}
        </div>

        {/* Products Grid */}
        <div className="px-4 py-4">
          {filteredProducts.length > 0 ? (
            <div className="grid grid-cols-2 gap-3">
              {filteredProducts.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 mb-4">No products found</p>
              {hasActiveFilters && (
                <Button variant="outline" onClick={clearFilters}>
                  Clear Filters
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Age Gate Modal */}
      <AgeGateModal 
        open={showAgeGate} 
        onOpenChange={(open) => {
          setShowAgeGate(open);
          if (!open && !ageVerified) {
            // User dismissed without verifying — go back to all
            setFilters(prev => ({ ...prev, category: 'all' }));
            setSearchParams({});
          }
        }}
        onVerified={handleAgeVerified}
      />
    </Layout>
  );
};

export default BrowseProductsPage;
